import React, { useState, useEffect, useCallback, useRef } from "react";
import { Auth } from "aws-amplify";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoading } from "../../../context/LoadingContext";
import { getParameterFromManager } from "../../../services/Utils";
import { v4 as uuidv4 } from "uuid";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import AlertModal from "../../Requirement/components/common/AlerModel";
import {
  fetchData,
  postQuery,
  searchfetchQueries,
} from "../../Requirement/services/RequirementServices";
import {
  FormControl,
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
} from "@mui/material";

const ProcurementProjects = () => {
  const { setLoading } = useLoading();
  const location = useLocation();
  const navigate = useNavigate();
  const [rowStart, setRowStart] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [countrymaster, setCountryMaster] = useState([]);
  const [strategicPartners, setStrategicPartners] = useState([]);
  const [allProductList, setAllProductList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [searchProductGrades, setSearchProductGrades] = useState([]);
  const [formProductGrades, setFormProductGrades] = useState([]);
  const [activeUser, setActiveUser] = useState("");
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    autoClose: null,
  });
  const [searchValues, setSearchValues] = useState({
    searchProduct: "",
    searchProductGrade: "",
    searchCountry: "",
    searchProjectName: "",
  });
  const statusOptions = [
    { value: "", label: "ALL" },
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    ProjectName: "",
    ProductCategoryID: "",
    ProductID: "",
    Grade: "",
    Quantity: "",
    Country: "",
    TradeType: "",
    StrategicPartnerID: "",
  });

  const [errors, setErrors] = useState({});

  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, show: false }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.ProjectName.trim()) {
      newErrors.ProjectName = "Project Name is required";
    }
    if (!formData.Country) {
      newErrors.Country = "Country is required";
    }
    if (!formData.ProductID) {
      newErrors.ProductID = "Product is required";
    }
    if (!formData.Grade) {
      newErrors.Grade = "Grade is required";
    }
    if (!formData.StrategicPartnerID) {
      newErrors.StrategicPartnerID = "Strategic Partner is required";
    }
    if (!formData.TradeType) {
      newErrors.TradeType = "Trade Type is required";
    }
    if (
      !formData.Quantity ||
      isNaN(formData.Quantity) ||
      formData.Quantity <= 0
    ) {
      newErrors.Quantity = "Quantity must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Load strategic partners
  const loadStrategicPartners = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listAMTPLUsers",
          rowStart: 0,
          pageSize: 1,
          filterTerms: {
            UserType: "StrategicPartner",
          },
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allPartners] = await Promise.all([
          searchfetchQueries({
            target: "listAMTPLUsers",
            rowStart: 0,
            pageSize: totalCount,
            filterTerms: {
              UserType: "StrategicPartner",
            },
          }),
        ]);

        setStrategicPartners(allPartners);
      } else {
        setStrategicPartners([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);
  // Load countries
  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Country" },
        }),
      ]);
      if (allCountries.items.length > 0) {
        setCountryMaster(allCountries.items);
      } else {
        setCountryMaster([]);
      }
    } catch (error) {
      console.error("Error loading countries: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        setAllProductList(allProducts);
        const distinctProducts = Array.from(
          new Map(allProducts.map((p) => [p.ProductName, p])).values()
        );
        setProductList(distinctProducts);
      } else {
        setProductList([]);
        setAllProductList([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadSearchProductGrades = useCallback(
    async (productName) => {
      if (!productName) {
        setSearchProductGrades([]);
        return;
      }
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName },
          }),
        ]);
        setSearchProductGrades(allGrades.items || []);
      } catch (error) {
        console.error("Error loading search product grades: ", error);
        setSearchProductGrades([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const loadFormProductGrades = useCallback(
    async (productName) => {
      if (!productName) {
        setFormProductGrades([]);
        return;
      }
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName },
          }),
        ]);
        setFormProductGrades(allGrades.items || []);
      } catch (error) {
        console.error("Error loading form product grades: ", error);
        setFormProductGrades([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for the field being changed
    setErrors((prev) => ({ ...prev, [name]: "" }));

    // Load grades when product changes
    if (name === "ProductID") {
      loadFormProductGrades(value);
      setFormData((prev) => ({ ...prev, Grade: "" }));
    }
  };

  const saveProjectData = async (data) => {
    console.log("data@@@@", data);
    try {
      const body = isEditMode
        ? {
            target: "updateProjects",
            projectsInput: {
              ProjectID: data.ProjectID,
              ProjectName: data.ProjectName,
              Country: data.Country,
              ProductID: data.ProductID,
              Quantity: data.Quantity,
              TradeType: data.TradeType,
              StrategicPartnerID: data.StrategicPartnerID,
            },
          }
        : {
            target: "createProjects",
            projectsInput: {
              ProjectID: data.ProjectID,
              ProjectName: data.ProjectName,
              Country: data.Country,
              ProductID: data.ProductID,
              Quantity: data.Quantity,
              TradeType: data.TradeType,
              StrategicPartnerID: data.StrategicPartnerID,
            },
          };
      console.log("body@@", body);
      const result = await postQuery(body);
      if (result) {
        setModal({
          show: true,
          type: "success",
          title: "Success",
          message: `Project ${isEditMode ? "updated" : "added"} successfully!`,
          autoClose: false,
        });
        await fetchProjects(searchValues);
      }
    } catch (error) {
      console.error("Error saving Project:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `Failed to ${isEditMode ? "update" : "add"} Project.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      let product = allProductList.find(
        (item) =>
          item.ProductName === formData.ProductID &&
          item.Grade === formData.Grade
      );

      let pid = product ? product.ProductID : null;
      setFormData((prev) => ({ ...prev, ProductID: pid }));

      await saveProjectData({
        ...formData,
        ProductID: pid,
        ProjectID: isEditMode ? selectedProject.ProjectID : uuidv4(),
      });
      handleReset();
    }
  };

  const handleReset = () => {
    setFormData({
      ProjectName: "",
      ProductCategoryID: "",
      ProductID: "",
      Grade: "",
      Quantity: "",
      Country: "",
    });
    setErrors({});
    setIsEditMode(false);
    setSelectedProject(null);
    setShowForm(false);
    setFormProductGrades([]);
  };

  const fetchProjects = useCallback(
    async (searchValues, newRowStart = rowStart) => {
      setLoading(true);
      try {
        let pid="";
        if(searchValues.searchProduct && searchValues.searchProductGrade){
          let product = allProductList.find(
            (item) =>
              item.ProductName === searchValues.searchProduct &&
              item.Grade === searchValues.searchProductGrade
          );
          pid = product ? product.ProductID : null;
        }

        const body = {
          target: "listProjects",
          userOrder: "ProjectID",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            ProductID:pid,
            Country:searchValues.searchCountry || "",
            ProjectNamePartial:searchValues.searchProjectName || "",
          },
        };
        const response = await fetchData(body);
        console.log("response.items@@", response.items);
        const modifiedItems = (response.items || response).map((item) => ({
          ...item,
          id: item.ProjectID, // Add id property with value from ProjectID
        }));

        setProjects(modifiedItems);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading projects: ", error);
        setProjects([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, setLoading, searchValues]
  );

  const handleRowClick = (row) => {
    console.log('row@@',row);
    setSelectedProject(row);
    setIsEditMode(true);
    setShowForm(true);
    let product = allProductList.find(
      (item) =>
        item.ProductID === row.ProductID
    );

   
    setFormData({
      ProjectName: row.ProjectName || "",
      ProductID: product ? product.ProductName : "" || "",
      Grade:  product ? product.Grade : "" || "",
      Quantity: row.Quantity || "",
      Country: row.Country || "",
      StrategicPartnerID: row.StrategicPartnerID || "",
      TradeType: row.TradeType || "",
    });
    loadFormProductGrades(product ? product.ProductName : "" || "");
  };

  useEffect(() => {
    fetchProjects(searchValues);
  }, [fetchProjects, searchValues]);

  useEffect(() => {
    loadCountries();
    loadStrategicPartners();
    loadProductList();
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showForm, loadCountries, loadProductList, loadStrategicPartners]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        }
        await getParameterFromManager("INR_TO_USD");
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
  }, [navigate]);

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    fetchProjects(searchValues, newRowStart);
  };

  const handleSearch = (searchTerm) => {
    setSearchValues(searchTerm);
    setRowStart(0);
  };

  const columns = [
    { id: "ProjectName", numeric: false, label: "Project Name" },
    { id: "Country", numeric: false, label: "Country" },
    {
      id: "ProductID",
      numeric: false,
      label: "Product",
      renderCell: (row) => {
        const pn = allProductList.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.ProductName : row.ProductID;
      },
    },
    {
      id: "Grade",
      numeric: false,
      label: "Grade",
      renderCell: (row) => {
        const pn = allProductList.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.Grade : "";
      },
    },
    { id: "Quantity", numeric: true, label: "Quantity" },
    { id: "TradeType", numeric: false, label: "Trade Type" },
    {
      id: "StrategicPartnerID",
      numeric: false,
      label: "Strategic Partner",
      renderCell: (row) => {
        const sp = strategicPartners.find(
          (item) => item.AMTPLUserID === row.StrategicPartnerID
        );
        return sp ? sp.FirstName + " " + sp.LastName : "";
      },
    },
  ];

  const fields = [
    {
      name: "searchProjectName",
      label: "Project Name",
      type: "text",
      value: searchValues.searchProjectName,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, searchProjectName: value })),
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: productList.map((item) => ({
        value: item.ProductName,
        label: item.ProductName,
      })),
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          searchProduct: value,
          searchProductGrade: "",
        }));
        loadSearchProductGrades(value);
      },
    },
    {
      name: "searchProductGrade",
      label: "Product Grade",
      type: "dropdown",
      options: searchProductGrades.map((item) => ({
        value: item.Grade,
        label: item.Grade,
      })),
      value: searchValues.searchProductGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProductGrade: value }));
      },
    },
    {
      name: "searchCountry",
      label: "Country",
      type: "dropdown",
      options: countrymaster.map((item) => ({
        value: item.Value,
        label: item.Value,
      })),
      value: searchValues.searchCountry,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          searchCountry: value,
        }));
      },
    },
  ];
  const currentPage = Math.floor(rowStart / pageSize) + 1;
  return (
    <div className="amtpl-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card">
        <div className="container-fluid">
          <div className="search-filter4">
            <div className="title4">
              <div className="head-text">Projects</div>
            </div>
            <div className="filter4">
              <SearchFilter fields={fields} onSearch={handleSearch} />
            </div>
            <div
              className="head-btn"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <div
                className="submit-btn"
                onClick={() => {
                  console.log("Add button clicked, setting showForm to true");
                  setShowForm(true);
                  setIsEditMode(false);
                  setFormData({
                    ProjectName: "",
                    ProductCategoryID: "",
                    ProductID: "",
                    Grade: "",
                    Quantity: "",
                    Country: "",
                  });
                  setErrors({});
                  setFormProductGrades([]);
                }}
              >
                Add
              </div>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={projects}
            total={totalItems}
            title="Procurement Project List"
            rowsPerPage={pageSize}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            selectedRows={selectedProject ? [selectedProject.ProjectID] : []}
          />
          <div className="table-header">
            {showForm && (
              <div className="header-text">
                <div className="head-text">
                  {isEditMode ? "Modify" : "Add"} Procurement Project
                </div>
              </div>
            )}
            <div className="head-btn">
              {showForm && (
                <>
                  <div onClick={handleSubmit} className="submit-btn">
                    {isEditMode ? "Update" : "Submit"}
                  </div>
                  <div onClick={handleReset} className="submit-btn">
                    {isEditMode ? "Cancel" : "Reset"}
                  </div>
                </>
              )}
            </div>
          </div>

          {showForm && (
            <div className="row" ref={formRef}>
              <form onSubmit={handleSubmit} style={{ padding: "20px" }}>
                <div className="row">
                  <div className="col-md-3">
                    <label htmlFor="ProjectName">Project Name</label>
                    <TextField
                      id="ProjectName"
                      name="ProjectName"
                      value={formData.ProjectName}
                      onChange={handleChange}
                      fullWidth
                      required
                      error={!!errors.ProjectName}
                      helperText={errors.ProjectName}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="Country">Country</label>
                    <FormControl fullWidth error={!!errors.Country}>
                      <Select
                        id="Country"
                        name="Country"
                        value={formData.Country}
                        onChange={handleChange}
                        required
                      >
                        {countrymaster.map((country) => (
                          <MenuItem key={country.Value} value={country.Value}>
                            {country.Value}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.Country && (
                        <FormHelperText>{errors.Country}</FormHelperText>
                      )}
                    </FormControl>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="ProductID">Product</label>
                    <FormControl fullWidth error={!!errors.ProductID}>
                      <Select
                        id="ProductID"
                        name="ProductID"
                        value={formData.ProductID}
                        onChange={handleChange}
                        required
                      >
                        {productList.map((product) => (
                          <MenuItem
                            key={product.ProductName}
                            value={product.ProductName}
                          >
                            {product.ProductName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.ProductID && (
                        <FormHelperText>{errors.ProductID}</FormHelperText>
                      )}
                    </FormControl>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="Grade">Grade</label>
                    <FormControl fullWidth error={!!errors.Grade}>
                      <Select
                        id="Grade"
                        name="Grade"
                        value={formData.Grade}
                        onChange={handleChange}
                        required
                      >
                        {formProductGrades.map((grade) => (
                          <MenuItem key={grade.Grade} value={grade.Grade}>
                            {grade.Grade}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.Grade && (
                        <FormHelperText>{errors.Grade}</FormHelperText>
                      )}
                    </FormControl>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="Quantity">Quantity</label>
                    <TextField
                      id="Quantity"
                      name="Quantity"
                      type="number"
                      value={formData.Quantity}
                      onChange={handleChange}
                      fullWidth
                      required
                      error={!!errors.Quantity}
                      helperText={errors.Quantity}
                      inputProps={{ min: 0 }}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="StrategicPartnerID">
                      Strategic Partner
                    </label>
                    <FormControl fullWidth error={!!errors.StrategicPartnerID}>
                      <Select
                        id="StrategicPartnerID"
                        name="StrategicPartnerID"
                        value={formData.StrategicPartnerID}
                        onChange={handleChange}
                        required
                      >
                        {strategicPartners.map((sp) => (
                          <MenuItem key={sp.AMTPLUserID} value={sp.AMTPLUserID}>
                            {sp.FirstName} {sp.LastName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.StrategicPartnerID && (
                        <FormHelperText>
                          {errors.StrategicPartnerID}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="TradeType">Trade Type</label>
                    <FormControl fullWidth error={!!errors.TradeType}>
                      <Select
                        id="TradeType"
                        name="TradeType"
                        value={formData.TradeType}
                        onChange={handleChange}
                        required
                      >
                        <MenuItem key="Import" value="Import">
                          Import
                        </MenuItem>
                        <MenuItem key="Export" value="Export">
                          Export
                        </MenuItem>
                        <MenuItem key="Both" value="Both">
                          Both
                        </MenuItem>
                      </Select>
                      {errors.TradeType && (
                        <FormHelperText>{errors.TradeType}</FormHelperText>
                      )}
                    </FormControl>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcurementProjects;
